import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, Coins, Receipt, WarningCircle, TrendUp, FileArrowDown, EnvelopeSimple, X, Plus, Trash, Globe, FileText, MapPin } from "@phosphor-icons/react";

const CCY_SYM = { EUR: "\u20ac", GBP: "\u00a3", USD: "$" };
const CCY_COLOR = { EUR: "#00A86B", GBP: "#5B8DEF", USD: "#C77DFF" };
const eur = (n) => `\u20ac${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const KPI = ({ label, value, sub, icon: Icon, color = "#FFB020", tid, i = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}
    data-testid={tid}
    className="glass rounded-xl p-6 h-full"
  >
    <div className="flex items-start justify-between">
      <div className="eyebrow">{label}</div>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 bg-black/30">
        <Icon size={18} weight="duotone" color={color} />
      </div>
    </div>
    <div className="font-display text-3xl mt-4 mono">{value}</div>
    {sub && <div className="text-xs text-white/50 mt-2">{sub}</div>}
  </motion.div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [grants, setGrants] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [mc, setMc] = useState(null);
  const [nexus, setNexus] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, g, r, m, nx] = await Promise.all([
        api.get("/dashboard/summary"), api.get("/grants"), api.get("/receipts"), api.get("/dashboard/multicurrency"),
        api.get("/compliance/us/nexus-overview").catch(() => ({ data: { enabled: false, states: [] } })),
      ]);
      setSummary(s.data);
      setGrants(g.data);
      setReceipts(r.data);
      setMc(m.data);
      setNexus(nx.data);
    })();
  }, []);

  const spent = summary?.total_grant_spent || 0;
  const remaining = Math.max(0, (summary?.total_awarded || 0) - spent);
  const pieData = [
    { name: "Spent", value: spent, color: "#FFB020" },
    { name: "Remaining", value: remaining, color: "#00A86B" },
  ];

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Compliance Overview</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-white/60 mt-2 text-sm">Your books, guardianed. All actions are logged for audit.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 glass rounded-full px-4 py-2 text-xs">
            <div className="w-2 h-2 rounded-full bg-emerald-pass animate-pulse" style={{ background: "#00A86B" }} />
            <span className="text-white/70">Compliance score:</span>
            <span className="mono font-semibold" style={{ color: "#00A86B" }}>{summary?.compliance_score ?? 100}%</span>
          </div>
          <ReportCardMenu />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI tid="kpi-grants" i={0} label="Active Grants" value={summary?.grants_count ?? 0} sub={`€${(summary?.total_awarded||0).toLocaleString()} awarded`} icon={ShieldCheck} />
        <KPI tid="kpi-utilisation" i={1} label="Grant Utilisation" value={`${summary?.grant_utilization_pct ?? 0}%`} sub={`€${(summary?.total_grant_spent||0).toLocaleString()} spent`} icon={Coins} color="#00A86B" />
        <KPI tid="kpi-receipts" i={2} label="Receipts Captured" value={summary?.receipts_count ?? 0} sub={`${summary?.receipts_pass ?? 0} passing`} icon={Receipt} />
        <KPI tid="kpi-flags" i={3} label="Open Flags" value={summary?.receipts_flagged ?? 0} sub="Alcohol / BIK / VAT" icon={WarningCircle} color="#E63946" />
      </div>

      {/* Cross-border position */}
      {mc && <MultiCurrencyCard mc={mc} />}

      {/* US nexus watch */}
      {nexus && <NexusWatchCard overview={nexus} />}

      {/* Chart + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="glass rounded-xl p-6 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow">Grant Balance</div>
            <TrendUp size={18} weight="duotone" color="#FFB020" />
          </div>
          <div className="h-48">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} innerRadius={55} outerRadius={80} paddingAngle={4} dataKey="value">
                  {pieData.map((e) => <Cell key={e.name} fill={e.color} stroke="none" />)}
                </Pie>
                <Tooltip contentStyle={{ background:"#0A1311", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10, color:"#F3F5F4" }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><div className="text-[10px] uppercase text-white/50 tracking-wider">Spent</div><div className="mono text-lg" style={{ color: "#FFB020" }}>€{spent.toLocaleString()}</div></div>
            <div><div className="text-[10px] uppercase text-white/50 tracking-wider">Remaining</div><div className="mono text-lg" style={{ color: "#00A86B" }}>€{remaining.toLocaleString()}</div></div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="glass rounded-xl p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow">Recent Receipts</div>
            <a href="/receipts" className="text-xs text-white/60 hover:text-white">View all →</a>
          </div>
          {receipts.length === 0 ? (
            <div className="text-white/50 text-sm py-6 text-center">No receipts yet. Add one from the Receipts tab.</div>
          ) : (
            <table className="data">
              <thead><tr><th>Vendor</th><th>Category</th><th>Date</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
              <tbody>
                {receipts.slice(0, 6).map(r => (
                  <tr key={r.receipt_id}>
                    <td>{r.vendor}</td>
                    <td className="text-white/60">{r.category}</td>
                    <td className="text-white/60">{r.date}</td>
                    <td className="num">€{Number(r.amount).toLocaleString()}</td>
                    <td><span className={`badge-${r.status} px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>
      </div>

      {/* Active Grants */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">Active Grants</div>
          <a href="/grants" className="text-xs text-white/60 hover:text-white">Manage →</a>
        </div>
        {grants.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center text-white/60 text-sm">
            No grants yet. <a href="/grants" className="text-[#FFB020] underline">Create your first grant</a> to start guardianing.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {grants.slice(0,6).map((g,i) => {
              const pct = g.total_awarded > 0 ? (g.spent / g.total_awarded) * 100 : 0;
              let pctBadge = "badge-pass";
              if (pct > 90) pctBadge = "badge-fail";
              else if (pct > 70) pctBadge = "badge-warn";
              return (
                <motion.div key={g.grant_id}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i*0.05 }}
                  className="glass rounded-xl p-6 hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-white/50">{g.programme}</div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${pctBadge}`}>{pct.toFixed(0)}%</span>
                  </div>
                  <div className="font-display text-lg mt-2">{g.name}</div>
                  <div className="mono text-2xl mt-3">{({ EUR: "€", GBP: "£", USD: "$" }[g.currency] || "€")}{Number(g.remaining).toLocaleString()}<span className="text-white/40 text-sm ml-1">remaining</span></div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-4">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100,pct)}%`, background: pct > 90 ? "#E63946" : "linear-gradient(90deg,#00A86B,#FFB020)" }} />
                  </div>
                  {(g.milestone_total > 0 || g.drawdown_count > 0) && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5" data-testid={`dash-grant-footer-${g.grant_id}`}>
                      {g.milestone_total > 0 ? (
                        <div className="flex items-center gap-2">
                          <DashRing done={g.milestone_done} total={g.milestone_total} overdue={g.milestone_overdue} />
                          <span className="text-[11px] text-white/50">{g.milestone_overdue > 0 ? <span className="text-[#E63946]">{g.milestone_overdue} overdue</span> : "milestones"}</span>
                        </div>
                      ) : <span />}
                      {g.drawdown_count > 0 && (
                        <div className="text-[11px] text-white/50 text-right">Cash<br/><span className="mono" style={{ color: g.cash_position < 0 ? "#E63946" : "#00A86B" }}>{({ EUR: "€", GBP: "£", USD: "$" }[g.currency] || "€")}{Number(g.cash_position).toLocaleString()}</span></div>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function DashRing({ done, total, overdue = 0, size = 34 }) {
  const pct = total ? done / total : 0;
  const r = (size - 5) / 2;
  const c = 2 * Math.PI * r;
  const color = overdue > 0 ? "#E63946" : (total > 0 && done === total ? "#00A86B" : "#FFB020");
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="dash-milestone-ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3.5" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="53%" textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="9" fontFamily="monospace">{done}/{total}</text>
    </svg>
  );
}

function NexusWatchCard({ overview }) {
  const RAG = { red: "#E63946", amber: "#FFB020", green: "#00A86B", none: "#5C6B66" };
  const states = (overview?.states || []).filter((s) => s.applicable);
  if (!overview?.enabled || states.length === 0) return null;
  const label = (s) => s.status === "red" ? "Threshold reached" : s.status === "amber" ? "Approaching" : "Under threshold";
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="glass rounded-xl p-6" data-testid="nexus-watch-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 eyebrow"><MapPin size={14} weight="duotone" color="#C77DFF" /> US nexus watch</div>
        <a href="/compliance" className="text-xs text-white/60 hover:text-white" data-testid="nexus-watch-link">Manage →</a>
      </div>
      <p className="text-xs text-white/45 mb-5">How close each state&apos;s sales are to its sales-tax registration threshold. Indicative only — not tax advice.</p>
      <div className="space-y-2.5">
        {states.map((s, i) => {
          const c = RAG[s.status] || RAG.green;
          const bar = Math.min(100, s.gating_pct || 0);
          return (
            <div key={s.code} className="rounded-xl bg-black/25 border border-white/5 p-4" data-testid={`nexus-watch-${s.code}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold" style={{ background: `${c}22`, color: c }}>{i + 1}</span>
                  <span className="text-sm font-semibold">{s.name}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: c }}>{label(s)}</span>
                </div>
                <span className="mono text-sm font-semibold" style={{ color: c }} data-testid={`nexus-watch-pct-${s.code}`}>{s.gating_pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${bar}%`, background: c }} />
              </div>
              {s.sales_threshold ? (
                <div className="text-[11px] text-white/45 mt-1.5">${Number(s.sales_entered).toLocaleString()} of ${Number(s.sales_threshold).toLocaleString()} threshold</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function MultiCurrencyCard({ mc }) {
  const spend = mc.spend?.by_currency || [];
  const invBase = mc.invoices?.total_base || 0;
  const outBase = mc.invoices?.outstanding_base || 0;
  const invCcy = mc.invoices?.by_currency || [];
  const hasData = spend.length > 0 || invCcy.length > 0;
  if (!hasData) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="glass rounded-xl p-6" data-testid="multicurrency-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 eyebrow"><Globe size={14} weight="duotone" color="#FFB020" /> Cross-border position</div>
        <div className="flex items-center gap-1.5">
          {(mc.currencies || []).map((c) => (
            <span key={c} className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider"
              style={{ background: `${CCY_COLOR[c] || "#8A9A94"}22`, color: CCY_COLOR[c] || "#8A9A94", border: `1px solid ${CCY_COLOR[c] || "#8A9A94"}` }}>{c}</span>
          ))}
        </div>
      </div>
      <p className="text-xs text-white/45 mb-5">All figures in your EUR reporting base, converted at the ECB rate on each transaction date. Native amounts shown for reference.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Spend */}
        <div className="rounded-xl bg-black/25 border border-white/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 flex items-center gap-1.5"><Coins size={13} /> Tracked spend</div>
          <div className="mono text-3xl mt-2" style={{ color: "#FFB020" }} data-testid="mc-spend-base">{eur(mc.spend?.base_total)}</div>
          <div className="space-y-1.5 mt-4">
            {spend.map((s) => (
              <div key={s.currency} className="flex items-center justify-between text-sm" data-testid={`mc-spend-${s.currency}`}>
                <span className="flex items-center gap-2 text-white/70">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: CCY_COLOR[s.currency] || "#8A9A94" }} />
                  {CCY_SYM[s.currency] || ""}{Number(s.original_total).toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-white/40 text-xs">{s.currency}</span>
                </span>
                <span className="mono text-white/60">{eur(s.base_total)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Invoiced */}
        <div className="rounded-xl bg-black/25 border border-white/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 flex items-center gap-1.5"><FileText size={13} /> Invoiced</div>
          <div className="mono text-3xl mt-2" style={{ color: "#00A86B" }} data-testid="mc-inv-base">{eur(invBase)}</div>
          <div className="space-y-1.5 mt-4">
            {invCcy.length === 0 ? <div className="text-white/35 text-xs">No invoices yet.</div> : invCcy.map((s) => (
              <div key={s.currency} className="flex items-center justify-between text-sm" data-testid={`mc-inv-${s.currency}`}>
                <span className="flex items-center gap-2 text-white/70">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: CCY_COLOR[s.currency] || "#8A9A94" }} />
                  {CCY_SYM[s.currency] || ""}{Number(s.total).toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-white/40 text-xs">{s.currency}</span>
                </span>
                <span className="mono text-white/60">{eur(s.total_base)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Outstanding */}
        <div className={`rounded-xl bg-black/25 border p-5 ${outBase > 0 ? "border-[#FFB020]/40" : "border-white/5"}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 flex items-center gap-1.5"><WarningCircle size={13} /> Outstanding receivables</div>
          <div className="mono text-3xl mt-2" style={{ color: outBase > 0 ? "#FFB020" : "#8A9A94" }} data-testid="mc-outstanding-base">{eur(outBase)}</div>
          <div className="text-xs text-white/45 mt-4">
            {outBase > 0 ? "Draft & sent invoices awaiting payment, in your EUR base." : "Nothing outstanding — every invoice is paid or void."}
          </div>
          {mc.invoices?.oldest_overdue_days > 0 && (
            <div className="mt-3 rounded-lg px-3 py-2 text-xs" style={{ background: "#E6394614", border: "1px solid #E6394644" }} data-testid="ar-oldest-overdue">
              <span className="text-[#E63946] font-semibold">{eur(mc.invoices.oldest_overdue_base)}</span>
              <span className="text-white/70"> overdue by {mc.invoices.oldest_overdue_days} days</span>
              <span className="text-white/45"> · {eur(mc.invoices.overdue_base)} total overdue</span>
            </div>
          )}
          <a href="/invoices" className="inline-flex items-center gap-1 text-xs text-[#FFB020] hover:underline mt-3" data-testid="mc-invoices-link">Manage invoices →</a>
        </div>
      </div>
    </motion.div>
  );
}

function ReportCardMenu() {
  const [open, setOpen] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const options = [
    { d: 30, label: "Last 30 days" },
    { d: 90, label: "Last 90 days" },
    { d: 180, label: "Last 6 months" },
    { d: 365, label: "Last 12 months" },
  ];
  const download = (d) => {
    window.open(`${API}/reports/integration-card.pdf?days=${d}`, "_blank");
    setOpen(false);
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(v => !v)} className="btn-gold text-xs" data-testid="btn-report-card">
        <FileArrowDown size={14} weight="fill"/> Report Card PDF
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="absolute right-0 top-full mt-2 glass rounded-xl p-2 z-50 min-w-[210px] shadow-2xl">
            <div className="eyebrow px-3 py-1 text-[10px]">Download</div>
            {options.map(o => (
              <button
                key={o.d}
                onClick={() => download(o.d)}
                className="w-full text-left px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/6 hover:text-white transition"
                data-testid={`report-card-${o.d}`}
              >
                {o.label}
              </button>
            ))}
            <div className="h-px bg-white/8 my-1"/>
            <button
              onClick={() => { setOpen(false); setEmailModal(true); }}
              className="w-full text-left px-3 py-2 rounded-lg text-sm text-[#FFB020] hover:bg-[#FFB020]/8 transition flex items-center gap-2"
              data-testid="report-card-email"
            >
              <EnvelopeSimple size={14} weight="fill"/> Send by email…
            </button>
          </div>
        </>
      )}
      {emailModal && <SendReportModal onClose={() => setEmailModal(false)}/>}
    </div>
  );
}

function SendReportModal({ onClose }) {
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [adhoc, setAdhoc] = useState("");
  const [days, setDays] = useState(90);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");

  const loadRecipients = useCallback(async () => {
    const r = await api.get("/recipients");
    setRecipients(r.data);
    setSelected(new Set(r.data.map(x => x.email)));
  }, []);
  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const addRecipient = async () => {
    if (!newEmail.includes("@")) return;
    await api.post("/recipients", { email: newEmail, name: newName, role: "board" });
    setNewEmail(""); setNewName("");
    loadRecipients();
  };
  const remove = async (id) => {
    await api.delete(`/recipients/${id}`);
    loadRecipients();
  };
  const toggle = (email) => {
    const s = new Set(selected);
    s.has(email) ? s.delete(email) : s.add(email);
    setSelected(s);
  };
  const send = async () => {
    const to = [...selected];
    if (adhoc.trim() && adhoc.includes("@")) to.push(adhoc.trim());
    if (!to.length) { alert("Pick or add at least one recipient"); return; }
    setSending(true); setResult(null);
    try {
      const r = await api.post("/reports/send", { days, to, note });
      setResult(r.data);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="glass rounded-2xl w-full max-w-xl my-8 p-8" data-testid="send-report-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="eyebrow mb-1">Reports</div>
            <div className="font-display text-2xl">Send Report Card by email</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="btn-close-send"><X size={20}/></button>
        </div>

        <div className="space-y-4">
          <div>
            <label>Period</label>
            <select value={days} onChange={e => setDays(Number(e.target.value))} data-testid="select-days">
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last 12 months</option>
            </select>
          </div>

          <div>
            <label>Saved recipients</label>
            {recipients.length === 0 ? (
              <div className="text-white/40 text-xs py-3">No saved recipients yet.</div>
            ) : (
              <div className="space-y-1 mt-1">
                {recipients.map(r => (
                  <label key={r.recipient_id} className="flex items-center gap-3 p-2 rounded-lg bg-white/3 hover:bg-white/6 cursor-pointer" data-testid={`recipient-${r.recipient_id}`}>
                    <input type="checkbox" checked={selected.has(r.email)} onChange={() => toggle(r.email)} className="w-auto"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{r.name || r.email}</div>
                      <div className="text-[10px] text-white/45 truncate">{r.email} · {r.role}</div>
                    </div>
                    <button onClick={(e) => { e.preventDefault(); remove(r.recipient_id); }} className="text-white/40 hover:text-[#E63946]"><Trash size={13}/></button>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <div className="md:col-span-2">
              <label>Add recipient</label>
              <input type="email" placeholder="board@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} data-testid="input-new-email"/>
            </div>
            <button onClick={addRecipient} className="btn-ghost text-xs justify-center" data-testid="btn-add-recipient">
              <Plus size={12} weight="bold"/> Save
            </button>
          </div>
          <div>
            <label>Or one-off email (not saved)</label>
            <input type="email" placeholder="ad-hoc@example.com" value={adhoc} onChange={e => setAdhoc(e.target.value)} data-testid="input-adhoc"/>
          </div>
          <div>
            <label>Note (optional)</label>
            <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Q1 board update" data-testid="input-note"/>
          </div>

          {result && (
            <div className={`rounded-xl p-3 text-xs border ${result.failed > 0 ? "bg-[#E63946]/10 border-[#E63946]/40 text-[#E63946]" : "bg-[#00A86B]/10 border-[#00A86B]/40 text-[#00A86B]"}`} data-testid="send-result">
              Sent: {result.sent} · Failed: {result.failed}
              {result.results?.filter(r => r.status === "error").slice(0,2).map((r,i) => (
                <div key={i} className="mt-1 opacity-80">{r.to}: {r.error}</div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t border-white/5">
            <button onClick={onClose} className="btn-ghost">Close</button>
            <button onClick={send} disabled={sending} className="btn-gold" data-testid="btn-send-report">
              {sending ? "Sending…" : <><EnvelopeSimple size={14} weight="fill"/> Send</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
